/**
 * Quiz Kit (Shopify app) renders its own "Add to Cart" button on the quiz
 * result screen, wired to its own direct add-to-cart call. That call
 * bypasses buy-buttons.liquid entirely, so it never sees this theme's
 * pre-order / sold-out handling -- for a variant flagged pre-order with zero
 * real inventory (e.g. Cambie), that direct add silently fails: cart stays
 * empty, no error shown.
 *
 * This fixes it per-case rather than always redirecting away from the quiz:
 *  - Regular, in-stock match: do nothing. Quiz Kit's own add-to-cart already
 *    works correctly for this case today.
 *  - Pre-order match: relabel the button "Pre-order", and on click, open
 *    PreProduct's own real pledge modal right over the quiz page -- by
 *    fetching the actual PDP's buy-buttons markup for that variant (same
 *    fetch-and-parse pattern used elsewhere in this theme, e.g.
 *    hidden-variant-redirect.liquid), injecting it invisibly, nudging
 *    PreProduct's own sitewide script (window.PPretry()), and clicking
 *    *that* button. Confirmed empirically (2026-10-07, read-only against
 *    the live site) that PreProduct's app-embed script is loaded sitewide
 *    (not scoped to product templates) and opens its real modal correctly
 *    when given this markup, from any page.
 *  - Sold-out (not pre-order, zero stock) match: redirect to the PDP, where
 *    Sold Out / Notify Me / Altitude referral already render correctly.
 *    Confirmed with the user directly rather than inventing new in-quiz UI
 *    for this case.
 *  - Classification hasn't resolved yet (fetch still in flight) when the
 *    shopper clicks: fail toward the PDP redirect, the same as sold-out --
 *    never silently let an unclassified click fall through to Quiz Kit's
 *    native (possibly broken) handler.
 *
 * Scoped strictly to `.quiz-kit-section` (the wrapper class Shopify adds to
 * any app-block section): `data-variant-id` is also used elsewhere in this
 * theme (snippets/option-value.liquid's swatch inputs) for an unrelated
 * purpose, so this must never fire outside the quiz.
 */
(function () {
  if (window.__hettasQuizKitHandoffBound) return;
  window.__hettasQuizKitHandoffBound = true;

  var STATE = { PREORDER: "preorder", SOLD_OUT: "sold-out", AVAILABLE: "available" };
  var classificationCache = {}; // variantId -> Promise<{ state, formHtml }>

  function findProductLink(startEl, boundaryEl) {
    var node = startEl;
    var link = null;
    while (node && node !== boundaryEl) {
      link = node.querySelector('a[href*="/products/"]');
      if (link) break;
      node = node.parentElement;
    }
    return link;
  }

  // Swap only the text node that says "Add to Cart" / "Pre-order", leaving
  // any nested icon/animation markup (Quiz Kit wraps the label in its own
  // <span> alongside a checkmark <svg>) untouched.
  function relabelButton(button, text) {
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      if (/add to cart|pre-?order/i.test(node.nodeValue.trim())) {
        node.nodeValue = node.nodeValue.replace(/add to cart|pre-?order/i, text);
        return;
      }
    }
  }

  function classifyVariant(variantId, productUrlNoQuery) {
    if (classificationCache[variantId]) return classificationCache[variantId];
    var sep = productUrlNoQuery.indexOf("?") === -1 ? "?" : "&";
    var promise = fetch(productUrlNoQuery + sep + "variant=" + encodeURIComponent(variantId))
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var productForm = doc.querySelector("product-form");
        if (!productForm) return { state: STATE.AVAILABLE, formHtml: null };

        var preorderBtn = productForm.querySelector("[data-native-pre-order-btn]");
        if (preorderBtn) {
          return { state: STATE.PREORDER, formHtml: productForm.outerHTML };
        }

        var submitBtn = productForm.querySelector('[type="submit"]');
        var isDisabled = !!(submitBtn && submitBtn.hasAttribute("disabled"));
        return { state: isDisabled ? STATE.SOLD_OUT : STATE.AVAILABLE, formHtml: null };
      })
      .catch(function () {
        // Network/parse failure: don't cache it (let a later click retry the
        // fetch instead of being permanently stuck), and treat this attempt
        // as unclassified so the click handler falls back to the safe PDP
        // redirect rather than guessing.
        delete classificationCache[variantId];
        return { state: null, formHtml: null };
      });
    classificationCache[variantId] = promise;
    return promise;
  }

  function scanQuizSection(quizSection) {
    var buttons = quizSection.querySelectorAll("[data-variant-id]");
    buttons.forEach(function (button) {
      var variantId = button.getAttribute("data-variant-id");
      if (!variantId || button.getAttribute("data-hettas-last-variant") === variantId) return;
      button.setAttribute("data-hettas-last-variant", variantId);
      button.removeAttribute("data-hettas-state");

      var productLink = findProductLink(button, quizSection);
      if (!productLink) return;
      var fullHref = productLink.getAttribute("href");
      button.setAttribute("data-hettas-product-url", fullHref);

      classifyVariant(variantId, fullHref.split("?")[0]).then(function (result) {
        // Stale: the shopper changed colour/size again before this resolved.
        if (button.getAttribute("data-hettas-last-variant") !== variantId) return;
        if (!result.state) return; // unclassified -- leave unmarked, click handler fails open to redirect
        button.setAttribute("data-hettas-state", result.state);
        if (result.formHtml) {
          button.__hettasFormHtml = result.formHtml;
        }
        if (result.state === STATE.PREORDER) {
          relabelButton(button, "Pre-order");
        }
      });
    });
  }

  function triggerPreProductFlow(button) {
    var formHtml = button.__hettasFormHtml;
    var fallbackUrl = button.getAttribute("data-hettas-product-url");

    if (!formHtml) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    // PreProduct's Liquid-rendered markup always names this callback
    // __preorderBtnVisCallback -- keep that literal name rather than
    // substituting a unique one, since PreProduct's own script appears to
    // key more than just this one call off it (see the stale-reference note
    // below for a related reason not to over-engineer this further).
    var callbackName = "__preorderBtnVisCallback";

    var container = document.createElement("div");
    container.style.cssText = "position:fixed; top:0; left:0; width:300px; height:100px; overflow:visible; opacity:0.01; pointer-events:none; z-index:-1;";
    container.innerHTML = formHtml;
    document.body.appendChild(container);

    if (!container.querySelector("[data-native-pre-order-btn]")) {
      container.remove();
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    var clicked = false;
    window[callbackName] = function (btn, show) {
      // PreProduct calls this more than once per attempt (observed in
      // testing: its own "Page Watcher" retries and re-fires this same
      // callback) -- deleting/replacing it after the first call meant a
      // later, possibly-authoritative call hit nothing. Keep it live for
      // this container's whole lifetime instead, and only act on it once.
      if (show && !clicked) {
        clicked = true;
        // PreProduct's own click-intercepting listener isn't necessarily
        // bound to this button yet just because it told us (via this very
        // callback) that the button should be visible -- clicking
        // immediately here raced it in testing (the click fell through to
        // the underlying native /cart/add.js form submit instead of
        // PreProduct's modal, which rejected it with a 422 since this
        // variant has no real inventory). A short buffer resolved it more
        // reliably.
        //
        // Also: query for the button fresh HERE, not once right after
        // injecting it -- PreProduct appears to clone/replace the button it
        // processes (this theme's own preorder-variant-sync.liquid has
        // comments describing exactly this behavior for the real PDP), so a
        // reference captured before PPretry() runs can go stale and end up
        // pointing at a plain, no-longer-intercepted element by click time.
        // This was the actual bug behind every failed attempt below, not
        // any of the timing/visibility/callback-identity theories tried
        // first.
        setTimeout(function () {
          var liveBtn = container.querySelector("[data-native-pre-order-btn]:not([data-native-pre-order-btn-added])") || container.querySelector("[data-native-pre-order-btn]");
          if (liveBtn) liveBtn.click();
        }, 600);
      } else if (!show && !clicked) {
        // PreProduct re-evaluated and decided this variant isn't actually
        // eligible after all -- shouldn't happen given this was just
        // fetched from the live PDP, but fail open to the PDP itself rather
        // than leaving the shopper stuck.
        container.remove();
        if (fallbackUrl) window.location.href = fallbackUrl;
      }
    };

    if (typeof window.PPretry === "function") {
      window.PPretry();
    }

    // The modal PreProduct opens renders independently into the document
    // (confirmed: it's its own top-level overlay, not nested inside this
    // container), so removing the injected trigger markup afterward doesn't
    // affect it.
    setTimeout(function () {
      container.remove();
      if (window[callbackName]) delete window[callbackName];
    }, 8000);
  }

  // The `.quiz-kit-section` wrapper is server-rendered, but isn't reliably
  // present in the DOM yet at the moment this deferred script runs (how the
  // app-block section actually gets attached ended up not mattering once
  // this was made robust to it either way) -- so this can't do a one-time
  // lookup at init and scope an observer to that specific element. Observe
  // `document.body` broadly instead and resolve `.quiz-kit-section` fresh on
  // every mutation; cheap enough for a single scoped selector check, and
  // correct regardless of exactly when/how the section is attached.
  function init() {
    var observer = new MutationObserver(function () {
      var quizSection = document.querySelector(".quiz-kit-section");
      if (quizSection) scanQuizSection(quizSection);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-variant-id"],
    });

    var immediate = document.querySelector(".quiz-kit-section");
    if (immediate) scanQuizSection(immediate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener(
    "click",
    function (event) {
      var quizSection = event.target.closest(".quiz-kit-section");
      if (!quizSection) return;

      var button = event.target.closest("[data-variant-id]");
      if (!button) return;

      var state = button.getAttribute("data-hettas-state");

      // Known in-stock, non-pre-order: let Quiz Kit's own click proceed
      // completely untouched -- this already works correctly today.
      if (state === STATE.AVAILABLE) return;

      event.preventDefault();
      event.stopPropagation();

      if (state === STATE.PREORDER) {
        triggerPreProductFlow(button);
        return;
      }

      // Sold-out, or not yet classified (fetch still in flight / failed):
      // redirect to the PDP rather than risk a silent native-add failure.
      var url = button.getAttribute("data-hettas-product-url") || (findProductLink(button, quizSection) || {}).href;
      if (url) window.location.href = url;
    },
    true
  );
})();
